import { AlertTriangle, RotateCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './ui';

/**
 * Red de seguridad de última instancia.
 *
 * Sin esto, un error de render en cualquier parte del árbol tira **toda** la
 * app: React desmonta hasta la raíz y la pantalla se queda en blanco, sin una
 * sola pista de qué pasó — se descubrió así un `motion.div` colado dentro del
 * `LazyMotion` en modo `strict` de `App.tsx`, que lanza a propósito en vez de
 * fallar en silencio, pero sin límite de error nadie veía el porqué. Un
 * boundary no arregla el error, pero convierte "pantalla en blanco, sin
 * pista" en "esto se rompió, y aquí está el motivo" — la diferencia entre un
 * bug que se reporta y uno que solo se puede intentar reproducir a ciegas.
 *
 * Solo los componentes de clase pueden ser boundaries — no hay equivalente en
 * hooks todavía.
 */
type State = { error: Error | null };

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Es la única forma de que este error llegue a algún sitio: no hay
    // pantalla que lo muestre por debajo.
    console.error('Error sin capturar en el panel:', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="grid min-h-screen place-items-center p-6">
        <div className="grid max-w-md gap-4 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="size-6" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Algo salió mal</h1>
            <p className="mt-1 text-sm opacity-70">
              El panel encontró un error inesperado y no puede seguir mostrando esta pantalla.
              Recargar suele arreglarlo; si se repite, avisa con el mensaje de abajo.
            </p>
          </div>
          <pre className="overflow-x-auto rounded bg-black/5 p-3 text-left text-xs leading-5 dark:bg-white/5">
            {error.message}
          </pre>
          <Button variant="primary" className="mx-auto" onClick={() => window.location.reload()}>
            <RotateCw className="size-4" />
            Recargar
          </Button>
        </div>
      </main>
    );
  }
}
